<template>
    <div :class="['location-block flex', classes]">
        <div class="w-full">
            <div class="flex w-full justify-between items-center">
                <div class="flex flex-row items-center">
                    <Icon
                        class="mr-2"
                        icon="up-logo-icon"
                        alt="U.P Location Icon" />
                    <span class="location-city">{{
                        bookingStore.state.display_name
                    }}</span>
                </div>

                <button
                    v-if="relatedGyms.length && isNewBooking"
                    class="location-selector-btn underline text-primary text-sm"
                    @click="openLocationsModal">
                    Change
                </button>
            </div>

            <span
                v-if="relatedGyms.length && isNewBooking"
                class="text-xs !leading-[1.5] text-[#717171] font-Open">
                {{
                    !bookingStore.state?.gym_switched
                        ? "No suitable slots? Click 'change' to view consultation availability at nearby U.P. locations."
                        : "Click 'change' to view consultation availability at nearby U.P. locations."
                }}
            </span>
        </div>

        <a
            :href="bookingStore.state.maps_url"
            target="_blank">
            <div class="flex w-full cursor-pointer">
                <Icon
                    class="mr-2"
                    icon="location-marker"
                    alt="U.P Location marker (Clock)" />
                <div>
                    <span class="location-address cursor-pointer font-Open">{{
                        formattedAddress
                    }}</span>
                </div>
            </div>
        </a>

        <div class="flex justify-between w-full">
            <div class="flex flex-row justify-start items-center">
                <Icon
                    class="mr-2"
                    icon="clock"
                    alt="U.P Location Icon (Clock)" />

                <span class="location-timeslot font-Open"
                    >{{
                        convertAzureTime(bookingStore.state.duration).minutes
                    }}
                    minutes</span
                >
            </div>

            <div
                class="ml-auto cursor-pointer md:m-0 mb-4"
                @click="openInfoModal">
                <span class="what-to-expect--info text-highlight font-Open"
                    >What to expect?</span
                >
            </div>
        </div>
    </div>

    <Modal
        :triggerCopy="'Open Modal'"
        :title="'Need to know'">
        <template #body>
            <div class="modal-content md:h-full w-full">
                <div class="pb-4">
                    <p class="text-lg font-semibold">What to expect:</p>
                    <p>
                        Finding your U.P gym: We recommend planning your visit
                        to U.P. ahead of time. Information about how to find us,
                        nearby parking, and convenient transport options can be
                        found on our website. A member of our team will be there
                        to meet and greet you upon arrival.
                    </p>
                </div>

                <div class="pb-4">
                    <p class="text-lg font-semibold">
                        What to expect during the consultation:
                    </p>
                    <p>
                        You'll have a 30-minute conversation with a member of
                        our training team in our private consultation room. It’s
                        your opportunity to discuss your personal goals,
                        understand how the process works, and see how we tailor
                        our method to you. You can explore the best package for
                        you, ask any questions, and take a tour of the gym
                        facilities.
                    </p>
                </div>

                <div class="pb-4">
                    <p class="text-lg font-semibold">What to bring:</p>
                    <p>
                        You don’t need to bring any gym clothing with you for
                        your consultation. Please feel free to come along in
                        your regular clothing.
                    </p>
                </div>
            </div>
        </template>
    </Modal>

    <div
        v-if="locationsModalVisible"
        class="locations-modal fixed top-0 bottom-0 left-0 right-0 w-screen h-screen z-50 overflow-hidden bg-[rgba(18,18,18,.9)] flex justify-center items-center">
        <div
            class="md:max-w-[630px] w-full m-auto wrapper max-h-screen md:max-h-[calc(100vh-48px)] box-border bg-white md:rounded">
            <div
                class="locations-modal__header border-b-2 bg-white z-50 border-[#e5e5e5] flex flex-row justify-center absolute top-0 left-0 right-0 md:relative pt-6 pb-4 px-4 box-border h-[64px]">
                <h5 class="font-bold">Nearby Locations</h5>
                <button
                    class="close-locations-modal cursor-pointer absolute bottom-4 right-4"
                    @click="closeLocationsModal">
                    <Icon
                        class="!w-[24px] pointer-events-none !h-[24px] block mx-auto"
                        icon="close"
                        alt="U.P Location Icon" />
                </button>
            </div>

            <div
                class="locations-modal__list max-h-screen box-border md:p-0 flex flex-col pt-[64px] md:max-h-[calc(100vh-64px)] h-screen md:h-auto">
                <div
                    class="locations-modal__list__header mb-2 p-4 pb-0 md:p-6 md:pb-0 md:border-none">
                    <h4 class="font-bold text-md">Select a location *</h4>
                    <p>
                        View consultation availability at nearby U.P. locations.
                    </p>
                </div>
                <ul
                    class="location-modal__list__items m-4 md:m-6 flex-grow overflow-auto">
                    <li
                        v-for="gym in relatedGyms"
                        :key="gym.location_id">
                        <label
                            class="relative mb-2 flex items-start special-request-yes cursor-pointer">
                            <input
                                type="radio"
                                :value="gym.location_id"
                                name="selectGym"
                                @click="switchToGym(gym.location_name)"
                                class="hidden" />
                            <div class="special-request-radio"></div>
                            <div class="ml-2">
                                <h6>{{ gym.location_name }}</h6>
                                <span v-if="gym.distance && gym.distance < 100" class="text-sm text-[#717171]"
                                    >{{ gym.distance }} miles away from
                                    {{ bookingStore.state.display_name }}</span
                                >
                            </div>
                        </label>
                    </li>
                </ul>
                <div
                    class="locations-modal__list__button p-4 md:p-6 border-t text-center flex flex-col justify-center box-border items-center">
                    <p class="text-sm mb-4">
                        By continuing, your current date and time selections
                        will be lost.
                    </p>
                    <button
                        class="action-btn flex items-center justify-center space-x-2 p-4 relative flex-1 self-stretch flex-grow-1 text-text-on-background rounded-full overflow-hidden w-full"
                        :class="[
                            !switchToGymName
                                ? 'bg-[#D0D0D0] pointer-events-none'
                                : 'bg-[#e40032]',
                        ]"
                        @click="navigateToSelectedGym"
                        :disabled="!switchToGymName">
                        <div
                            class="text-wrapper relative var-font-family whitespace-normal font-[--WEB-all-button-large-font-weight] text-[--colour-theme-text-text-on-background] var-line-height mt--1 w-fit md:whitespace-nowrap leading-6"
                            >
                            View availability
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useModalStore } from '@/stores/modals';
    import { useBookingStore } from '@/stores/booking';
    import { useStateStore } from '@/stores/state';
    import { convertAzureTime } from '@/helpers/azureDateTimeFormats';
    import { formatAddress } from '@/helpers/formatAddress';
    import {
        getBookingClusterForGym,
        findGymsWithSameCluster,
        ILocation,
		sortByDistanceToMainGym,
    } from '@/helpers/gymClusters';
    import { getAllLocations } from '@/helpers/fetchData';
    import { computed, onMounted, ref } from 'vue';
    import { useRouter } from 'nuxt/app';

    const { classes = 'flex', isNewBooking = false } = defineProps<{
        classes?: string;
        isNewBooking?: boolean;
    }>();

    const bookingStore = useBookingStore();
    const formattedAddress = computed(() => formatAddress(bookingStore.state.address));
    const locationsModalVisible = ref(false);
    const relatedGyms = ref<ILocation[]>([]);
    const switchToGymName = ref('');
    const redirectUrl = ref('');
    const route = ref(useRouter());
    const enquiry_id = useStateStore().state.request_id;

    onMounted(async () => {
        try {
            const location_id = bookingStore.state.location_id;
			const clusterTag = getBookingClusterForGym(bookingStore.state?.tags ?? []);

            if (!clusterTag) {
                return;
            }

            const allLocations = await getAllLocations();

            relatedGyms.value = findGymsWithSameCluster(
                allLocations,
                clusterTag,
                location_id
            );

			const sortedGyms = sortByDistanceToMainGym(relatedGyms.value, {
				latitude: bookingStore.state.latitude,
				longitude: bookingStore.state.longitude,
			});

			relatedGyms.value = sortedGyms;
        } catch (err: any) {
            throw new Error(err);
        }
    });

    const switchToGym = (gym_name: string) => {
        switchToGymName.value = gym_name;
		const enquiryQueryParam = enquiry_id ? `&enquiry_id=${enquiry_id}` : '';
        redirectUrl.value = `/location/${switchToGymName.value}?gym_switched=true&target_page=
		${route.value.currentRoute.name}${enquiryQueryParam}`;
    };

    const navigateToSelectedGym = () => {
        if (redirectUrl.value) {
            window.location.href = redirectUrl.value;
        }
    };

    const openLocationsModal = () => {
        locationsModalVisible.value = true;
    };
    const closeLocationsModal = () => {
        locationsModalVisible.value = false;
        switchToGymName.value = '';
        redirectUrl.value = '';
    };

    const openInfoModal = () => {
        const modalStore = useModalStore();
        modalStore.updateModal('open', true);
    };
</script>

<style scoped>
    .location-block {
        padding: var(--sizing-medium, 16px) var(--spacing-large, 16px);
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;
        border-radius: var(--radius-none, 0px);
        background: var(--background-bg-system, #fff);
        gap: 1rem;
    }

    .location-city {
        color: var(--text-text-default, #121212);

        /* WEB All/Body small */
        font-size: 14px;
        line-height: 20px; /* 142.857% */
    }

    .location-address {
        color: #121212;

        /* WEB All/Text link small */

        font-size: 14px;

        line-height: 20px; /* 142.857% */
        text-decoration-line: underline;
    }

    .location-timeslot {
        color: var(--text-text-default, #121212);

        /* WEB All/Body small */
        font-size: 14px;
        line-height: 20px; /* 142.857% */
    }

    .what-to-expect--info {
        color: var(--text-text-highlight, #5856d6);

        /* WEB All/Text link small */
        font-family: 'Open Sans';
        font-size: 14px;
        line-height: 20px; /* 142.857% */
        text-decoration-line: underline;
    }
</style>
